import { useEffect, useState } from "react";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { api, formatError } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { FileUploader, AttachmentList } from "../components/FileUploader";

export default function MedicalHistory() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [patientId, setPatientId] = useState(user?.role === "patient" ? user.id : "");
  const [patients, setPatients] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "consultation", title: "", content: "" });
  const [pendingFiles, setPendingFiles] = useState([]);

  useEffect(() => {
    if (user?.role === "doctor") {
      api.get("/users").catch(() => api.get("/doctors")).then(() => {});
      // Load patients via appointments to limit to those the doctor has seen
      api.get("/appointments").then(({ data }) => {
        const map = {};
        data.forEach((a) => { map[a.patient_id] = a.patient_name; });
        setPatients(Object.entries(map).map(([id, name]) => ({ id, name })));
      });
    }
  }, [user]);

  useEffect(() => {
    if (!patientId) return;
    api.get(`/history/${patientId}`).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [patientId]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data: entry } = await api.post("/history", { ...form, patient_id: patientId });
      if (pendingFiles.length > 0) {
        await api.post(`/history/${entry.id}/attachments`, { file_ids: pendingFiles.map((f) => f.id) });
      }
      toast.success(t("saved"));
      setOpen(false);
      setForm({ type: "consultation", title: "", content: "" });
      setPendingFiles([]);
      const { data } = await api.get(`/history/${patientId}`);
      setItems(data);
    } catch (err) { toast.error(formatError(err)); }
  };

  return (
    <div className="space-y-6 fade-up" data-testid="history-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-display text-3xl">{t("history")}</h1>
        <div className="flex items-center gap-3">
          {user?.role === "doctor" && (
            <Select value={patientId} onValueChange={setPatientId}>
              <SelectTrigger className="w-[260px]" data-testid="history-patient-select"><SelectValue placeholder={t("select_patient")} /></SelectTrigger>
              <SelectContent>
                {patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          {user?.role === "doctor" && patientId && (
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-primary hover:bg-primary/90" data-testid="add-history-btn"><Plus className="w-4 h-4 mr-2" /> {t("add_history")}</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{t("add_history")}</DialogTitle></DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div>
                    <Label>{t("type")}</Label>
                    <Select value={form.type} onValueChange={(v) => setForm((p) => ({ ...p, type: v }))}>
                      <SelectTrigger data-testid="hist-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="consultation">{t("consultation")}</SelectItem>
                        <SelectItem value="lab">{t("lab")}</SelectItem>
                        <SelectItem value="note">{t("note")}</SelectItem>
                        <SelectItem value="allergy">{t("allergy")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{t("title")}</Label><Input required value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} data-testid="hist-title" /></div>
                  <div><Label>{t("content")}</Label><Textarea required rows={5} value={form.content} onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))} data-testid="hist-content" /></div>
                  <div>
                    <Label>Adjuntos (fotos, PDFs, resultados)</Label>
                    <FileUploader files={pendingFiles} setFiles={setPendingFiles} purpose="history" testid="hist-attach" />
                  </div>
                  <Button type="submit" className="w-full bg-primary hover:bg-primary/90" data-testid="hist-save">{t("save")}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-display">{t("history")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {items.length === 0 && <div className="text-sm text-muted-foreground">{t("no_data")}</div>}
          {items.map((h) => (
            <div key={h.id} className="border border-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium">{h.title}</div>
                <Badge variant="outline">{t(h.type)}</Badge>
              </div>
              <div className="text-xs text-muted-foreground mb-2">{h.doctor_name} · {new Date(h.created_at).toLocaleString()}</div>
              <div className="text-sm whitespace-pre-wrap">{h.content}</div>
              {h.attachments && h.attachments.length > 0 && <AttachmentList items={h.attachments} />}
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
